import { SectionHeader } from '../common/section-header'
import { useSearchParams } from 'react-router-dom'

import { Badge, FieldHelp, Notice } from '@speaking-coach/ui'
import { CONTENT_HELP } from './content-help.js'
import { guidelinesApplyToLevel } from './content-model.js'
import { ContentRoute, ContentShell } from './content-shell.js'
import { ContentSelect } from './content-scope.js'
import {
  GuidelineLevelLists,
  GuidelineList,
} from './authoring-guidelines-components.js'
import type { AuthoringGuidelines, ContentDashboard } from './types.js'

export function ContentAuthoringGuidelinesPage(): JSX.Element {
  return (
    <ContentRoute>
      {(dashboard) => <AuthoringGuidelinesPage dashboard={dashboard} />}
    </ContentRoute>
  )
}

function AuthoringGuidelinesPage({
  dashboard,
}: {
  dashboard: ContentDashboard
}): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const levelOrder = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
  const levels = Array.from(new Set(dashboard.levels.map(row => row.level))).sort(
    (left, right) => levelOrder.indexOf(left) - levelOrder.indexOf(right),
  )
  const selectedLevel = searchParams.get('level') ?? ''
  return (
    <ContentShell
      title="Authoring guidelines"
      filters={
        <ContentSelect
          label="Level"
          value={levels.includes(selectedLevel) ? selectedLevel : ''}
          emptyLabel="Select a level"
          options={levels.map(level => ({ value: level, label: level }))}
          onChange={level =>
            setSearchParams(level ? { level } : {}, { replace: true })
          }
        />
      }
    >
      {levels.includes(selectedLevel) ? (
        <Guidelines
          guidelines={dashboard.authoringGuidelines}
          level={selectedLevel}
        />
      ) : (
        <Notice variant="neutral">
          Select a level to inspect authoring guidelines.
        </Notice>
      )}
    </ContentShell>
  )
}

function Guidelines({
  guidelines,
  level,
}: {
  guidelines: AuthoringGuidelines
  level: string
}): JSX.Element {
  const templates = guidelines.templates.filter((row) =>
    guidelinesApplyToLevel(row.levelRange, level),
  )
  const progressions = guidelines.missionProgressions.filter((row) =>
    guidelinesApplyToLevel(row.levelRange, level),
  )
  const qualityChecks = guidelines.qualityChecks
    .filter((row) => guidelinesApplyToLevel(row.levelRange, level))
    .flatMap((row) => row.checks)

  return (
    <section
      data-testid="content-authoring-guidelines"
      className="rounded-token-card border border-border-default bg-surface-elev p-5"
    >
      <SectionHeader
        level={3}
        title="Level guidelines"
        meta={<><FieldHelp label="level-specific guidelines" size="compact">
          {CONTENT_HELP.levelGuidelines}
        </FieldHelp><Badge variant="muted">Applies to {level}</Badge></>}
      />
      <p className="mt-1 text-body text-text-secondary">
        Start with CAN DO realism and learner fit, then use the structural checks
        to verify the lesson.
      </p>
      <div className="mt-4 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        <GuidelineList
          title="Quality and realism checks"
          items={qualityChecks}
          priority
        />
        <GuidelineList
          title="Human review"
          items={guidelines.humanReviewRules}
          priority
        />
        <GuidelineLevelLists
          title="Mission progression"
          rows={progressions}
          itemKey="missions"
        />
        <GuidelineLevelLists title="Template" rows={templates} itemKey="sections" />
        <GuidelineList title="House rules" items={guidelines.houseRules} />
      </div>
      <p className="mt-4 border-t border-border-default pt-3 text-caption text-text-secondary">
        Sources:{' '}
        {guidelines.sources.map((source, index) => (
          <span key={source.path}>
            {index > 0 ? ' · ' : null}
            <a
              href={source.url}
              target="_blank"
              rel="noreferrer"
              title={source.path}
              className="font-medium text-text-primary underline underline-offset-2"
            >
              {source.label}
            </a>
          </span>
        ))}
      </p>
    </section>
  )
}
